import { agentProfile } from "./agent-profiles";
import type { AgentRole } from "./agents";
import { assembleContextPacket, planLibraryContext, planWorldContext, retrieveWorldDocuments, type ContextPacket, type LibraryContextEntry } from "./context";
import type { WorldDocument } from "./types";
import type { AgentSystemPack } from "./system-packs";
import { INSTRUCTION_CAPSULE_MAX_CHARACTERS } from "./vfs";

export interface ContextMessage {
  id: string | number;
  role: "user" | "assistant";
  content: string;
}

export interface ContextCompilerBudget {
  modelContextTokens: number;
  outputReserveTokens: number;
  framingReserveTokens: number;
  staticTokens: number;
  worldTokens: number;
  libraryTokens: number;
  conversationTokens: number;
}

export interface CompileAgentContextRequest {
  role: AgentRole;
  query: string;
  sessionId: string;
  messages: readonly ContextMessage[];
  worldDocuments: readonly WorldDocument[];
  libraryEntries?: readonly LibraryContextEntry[];
  activeDocumentIds?: string[];
  activeLibraryEntryIds?: string[];
  platformInstruction?: string;
  experienceInstruction?: string;
  memorySnapshot?: string;
  budget?: Partial<ContextCompilerBudget>;
  epochSizeTurns?: number;
  maxWorldDocuments?: number;
  maxLibraryEntries?: number;
  maxLibraryEntriesPerKind?: number;
  systemPacks?: readonly AgentSystemPack[];
}

export interface CompiledAgentContext {
  role: AgentRole;
  packet: ContextPacket;
  instructionBlocks: { id: string; stability: string; content: string }[];
  messages: { role: "user" | "assistant"; content: string }[];
  manifest: {
    schemaVersion: 6;
    conversationEpoch: string;
    epochSizeTurns: number;
    epochIndex: number;
    turnInEpoch: number;
    historyBoundaryMessageId?: number;
    historyBoundaryReason: "retained_history" | "full_history";
    allocation: Record<string, number>;
    knowledgeCounts: { full: number; compact: number; dropped: number };
    sourceIds: string[];
  };
}

const DEFAULT_BUDGET: ContextCompilerBudget = {
  modelContextTokens: 128_000,
  outputReserveTokens: 16_384,
  framingReserveTokens: 2_048,
  staticTokens: 8_000,
  worldTokens: 12_000,
  libraryTokens: 8_000,
  conversationTokens: 48_000,
};
const tokenEstimate = (value: string) => Math.ceil(value.length / 3.4);
const characterBudget = (tokens: number) => Math.max(0, Math.floor(tokens * 3.4));

function retainConversation(messages: readonly ContextMessage[], tokenBudget: number): ContextMessage[] {
  const retained: ContextMessage[] = [];
  let used = 0;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]!;
    const cost = tokenEstimate(message.content) + 8;
    if (retained.length && used + cost > tokenBudget) break;
    if (!retained.length && cost > tokenBudget) {
      const allowed = characterBudget(Math.max(1, tokenBudget - 8));
      retained.unshift({ ...message, content: message.content.slice(-allowed) });
      break;
    }
    retained.unshift(message);
    used += cost;
  }
  return retained;
}

export function compileAgentContext(request: CompileAgentContextRequest): CompiledAgentContext {
  const profile = agentProfile(request.role);
  const budget: ContextCompilerBudget = { ...DEFAULT_BUDGET, ...request.budget };
  const usableTokens = Math.max(1_024, budget.modelContextTokens - budget.outputReserveTokens - budget.framingReserveTokens);
  const maxWorldDocuments = Math.max(0, Math.min(64, Math.trunc(request.maxWorldDocuments ?? 16)));
  const maxLibraryEntries = Math.max(0, Math.min(64, Math.trunc(request.maxLibraryEntries ?? 16)));
  const maxLibraryEntriesPerKind = Math.max(1, Math.min(16, Math.trunc(request.maxLibraryEntriesPerKind ?? 4)));
  const retrieval = retrieveWorldDocuments(request.worldDocuments, { query: request.query, ...(request.activeDocumentIds ? { activeDocumentIds: request.activeDocumentIds } : {}), limit: maxWorldDocuments });
  const world = planWorldContext(request.worldDocuments, {
    activeDocumentIds: request.activeDocumentIds ?? [], retrievedDocumentIds: retrieval.map((hit) => hit.documentId), maxCharacters: characterBudget(budget.worldTokens),
  });
  const library = planLibraryContext(request.libraryEntries ?? [], {
    query: request.query, ...(request.activeLibraryEntryIds ? { activeEntryIds: request.activeLibraryEntryIds } : {}), maxCharacters: characterBudget(budget.libraryTokens), maxEntries: maxLibraryEntries, maxPerKind: maxLibraryEntriesPerKind,
  });
  // Build stable and knowledge context first, then let conversation consume
  // the actual remainder. Reserving the configured conversation ceiling up
  // front can starve every invariant on smaller-context models.
  const packetTokenBudget = Math.min(usableTokens, budget.staticTokens + budget.worldTokens + budget.libraryTokens + 4_000);
  const packet = assembleContextPacket([
    { id: "agent_contract", label: "Agent contract", stability: "platform_static", content: `${profile.purpose}\n${profile.instruction}`, required: true, priority: 100, sourceIds: [`agent:${profile.id}`], maxCharacters: characterBudget(budget.staticTokens) },
    { id: "platform_instruction", label: "Platform instruction", stability: "platform_static", content: request.platformInstruction ?? "", required: true, priority: 95, sourceIds: ["platform"] },
    ...(request.role === "main_gm" ? [{
      id: "narrative_output_contract",
      label: "Narrative output contract",
      stability: "platform_static" as const,
      content: "<narrative-output>\nIn the narration phase, write clean literary prose and NPC dialogue based on confirmed outcomes. The application owns the turn header, clock, location name and scene image; do not generate duplicate headers, JSON beat fences, tool-call descriptions or bookkeeping markers. Preserve player agency: never control, speak for or decide actions for the player character. During decision or a pending roll, follow the phase instructions instead of producing narration.\n</narrative-output>",
      priority: 92,
      sourceIds: ["contract:narrative_output"],
    }] : []),
    { id: "experience_instruction", label: "Experience instruction", stability: "experience_static", content: request.experienceInstruction ?? "", priority: 90, sourceIds: ["experience"], maxCharacters: INSTRUCTION_CAPSULE_MAX_CHARACTERS },
    { id: "memory_snapshot", label: "Memory snapshot", stability: "session_dynamic", content: request.memorySnapshot ?? "", priority: 85, sourceIds: ["memory"] },
    { id: "world_context", label: "World context", stability: "turn_dynamic", content: world.map((item) => item.content).join("\n"), priority: 80, sourceIds: world.map((item) => item.documentId), maxCharacters: characterBudget(budget.worldTokens) },
    { id: "library_context", label: "Active library", stability: "turn_dynamic", content: library.map((item) => item.content).join("\n"), priority: 75, sourceIds: library.map((item) => item.entryId), maxCharacters: characterBudget(budget.libraryTokens) },
    ...(request.systemPacks ?? []).filter((pack) => pack.enabled && pack.instruction.trim()).map((pack, index) => ({
      id: `system_pack:${pack.id}`,
      label: pack.label,
      stability: "experience_static" as const,
      content: pack.instruction.trim(),
      priority: 70 - Math.min(index, 20),
      sourceIds: [`system-pack:${pack.id}`],
    })),
  ], characterBudget(packetTokenBudget));
  const retained = retainConversation(request.messages, Math.min(usableTokens - tokenEstimate(packet.sections.map((section) => section.content).join("\n")), budget.conversationTokens));
  const epochSizeTurns = Math.max(1, Math.min(64, Math.trunc(request.epochSizeTurns ?? 8)));
  const totalTurns = Math.floor(request.messages.length / 2);
  const epochIndex = Math.floor(totalTurns / epochSizeTurns);
  const boundary = retained[0];
  const staticStabilities = new Set(['platform_static','experience_static','session_stable']);
  const instructionBlocks = packet.sections.filter((section) => section.mode !== "omitted" && section.content).map((section) => ({ id: section.id, stability: section.stability, content: section.content }))
    .sort((a,b)=>Number(!staticStabilities.has(a.stability))-Number(!staticStabilities.has(b.stability)));
  const includedKnowledge = world.length + library.length;
  return {
    role: request.role, packet, instructionBlocks,
    messages: retained.map(({ role, content }) => ({ role, content })),
    manifest: {
      schemaVersion: 6,
      conversationEpoch: `${request.sessionId}:${epochIndex}`,
      epochSizeTurns,
      epochIndex,
      turnInEpoch: totalTurns % epochSizeTurns,
      ...(typeof boundary?.id === "number" ? { historyBoundaryMessageId: boundary.id } : {}),
      historyBoundaryReason: retained.length < request.messages.length ? "retained_history" : "full_history",
      allocation: {
        hardLimitTokens: budget.modelContextTokens,
        configuredHardLimitTokens: budget.modelContextTokens,
        activeWindowTokens: usableTokens,
        modelLimitTokens: budget.modelContextTokens,
        inputBudgetTokens: usableTokens,
        outputReserveTokens: budget.outputReserveTokens,
        framingReserveTokens: budget.framingReserveTokens,
        invariantTokens: tokenEstimate(packet.sections.find((section) => section.id === "agent_contract")?.content ?? ""),
        botStaticTokens: tokenEstimate(packet.sections.find((section) => section.id === "experience_instruction")?.content ?? ""),
        sessionStableTokens: 0,
        memoryTokens: tokenEstimate(packet.sections.find((section) => section.id === "memory_snapshot")?.content ?? ""),
        activeKnowledgeTokens: tokenEstimate(packet.sections.filter((section) => section.id === "world_context" || section.id === "library_context").map((section) => section.content).join("\n")),
        directiveTokens: tokenEstimate(request.query),
        conversationTokens: retained.reduce((sum, message) => sum + tokenEstimate(message.content), 0),
      },
      knowledgeCounts: { full: world.filter((item) => item.priority !== "retrieved").length + library.filter((item) => item.mode === "full").length, compact: world.filter((item) => item.priority === "retrieved").length + library.filter((item) => item.mode === "summary").length, dropped: Math.max(0, request.worldDocuments.length + (request.libraryEntries?.length ?? 0) - includedKnowledge) },
      sourceIds: packet.sourceIds,
    },
  };
}
